import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PieChart as PieIcon, BarChart3 } from "lucide-react";
import { Annotation } from "@/components/Annotation";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fM = (v) => v ? `${v.toFixed(1)}` : "0";
const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#f43f5e", "#06b6d4", "#ec4899", "#84cc16", "#0ea5e9", "#a855f7", "#f97316", "#14b8a6", "#6366f1", "#e11d48", "#22c55e"];

export default function RevenueCuts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/financial/revenue-cuts`).then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading || !data) return <div className="h-64 skeleton-shimmer rounded" />;

  const byChannel = data.by_channel || [];
  const byCategory = data.by_category || [];
  const topProducts = data.top_products || [];

  const channelPie = byChannel.map((c) => ({ name: c.channel, value: c.FY2025A || 0 })).filter(c => c.value > 0);
  const categoryBar = byCategory.filter(c => c.FY2025A > 0);

  return (
    <div className="space-y-5" data-testid="revenue-cuts-page">
      <div>
        <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">Revenue Breakdown</h1>
        <p className="text-sm text-slate-400 mt-1">Net revenue by channel, category &amp; top products &middot; INR Millions</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Channel Pie */}
        <Card className="lg:col-span-5 border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="channel-pie">
          <CardHeader className="pb-2">
            <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
              <PieIcon className="w-4 h-4 text-blue-400" />Revenue by Channel (FY25)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={channelPie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} stroke="#0f172a" strokeWidth={2}>
                  {channelPie.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
                      <p className="text-slate-200 font-semibold">{payload[0].name}</p>
                      <p className="text-slate-400 font-mono">INR {payload[0].value?.toFixed(1)}M</p>
                    </div>
                  );
                }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5 mt-2">
              {byChannel.map((c, i) => (
                <div key={c.channel} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORS[i] }} />
                    <span className="text-slate-300">{c.channel}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-slate-400">{fM(c.FY2025A)}M</span>
                    <span className="font-mono text-slate-500 text-[10px]">FY24: {fM(c.FY2024A)}M</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Category Bar */}
        <Card className="lg:col-span-7 border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="category-revenue-bar">
          <CardHeader className="pb-2">
            <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-400" />Revenue by Category (FY25)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={categoryBar} layout="vertical" margin={{ left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis dataKey="category" type="category" width={155} tick={{ fill: '#94a3b8', fontSize: 9 }} tickLine={false} axisLine={false} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
                      <p className="text-slate-200 font-semibold mb-1">{d.category}</p>
                      <p className="text-slate-400">FY25: <span className="font-mono text-slate-200">INR {fM(d.FY2025A)}M</span></p>
                      <p className="text-slate-400">FY24: <span className="font-mono text-slate-400">INR {fM(d.FY2024A)}M</span></p>
                      <p className="text-slate-400">Growth: <span className={`font-mono ${d.growth > 0 ? "text-emerald-400" : "text-rose-400"}`}>{d.growth > 0 ? "+" : ""}{d.growth}%</span></p>
                    </div>
                  );
                }} />
                <Bar dataKey="FY2025A" name="FY25 Revenue" radius={[0, 4, 4, 0]}>
                  {categoryBar.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Annotation>
        Baby Walker is the top category (INR {fM(byCategory.find(c => c.category === "Baby Walker")?.FY2025A)}M, +{byCategory.find(c => c.category === "Baby Walker")?.growth || 0}% YoY) followed by Bath Tub, Baby Furniture and High Chairs. Fast-growing categories like Safety (+124% YoY) and Baby Feeding (+117% YoY) demonstrate successful category expansion beyond core products. This diversification reduces concentration risk.
      </Annotation>

      {/* Top Products Table */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="top-products-table">
        <CardHeader className="pb-2">
          <CardTitle className="font-space text-sm font-semibold text-slate-200">Top Products by Revenue (FY25)</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left py-2 px-3 text-[10px] text-slate-500 uppercase w-8">#</th>
                <th className="text-left py-2 px-3 text-[10px] text-slate-500 uppercase">Product</th>
                <th className="text-left py-2 px-3 text-[10px] text-slate-500 uppercase">Category</th>
                <th className="text-right py-2 px-3 text-[10px] text-slate-500 uppercase">FY24</th>
                <th className="text-right py-2 px-3 text-[10px] text-slate-500 uppercase">FY25</th>
                <th className="text-right py-2 px-3 text-[10px] text-slate-500 uppercase">Growth</th>
              </tr>
            </thead>
            <tbody>
              {topProducts.map((p, i) => (
                <tr key={i} className="border-b border-slate-800/30 hover:bg-slate-800/20 transition-colors">
                  <td className="py-2 px-3 text-slate-500 font-mono text-xs">{i + 1}</td>
                  <td className="py-2 px-3 text-slate-200 text-xs">{p.product}</td>
                  <td className="py-2 px-3"><Badge variant="outline" className="text-[9px] text-slate-400 border-slate-700">{p.category}</Badge></td>
                  <td className="py-2 px-3 text-right font-mono text-xs text-slate-400">{fM(p.FY2024A)}M</td>
                  <td className="py-2 px-3 text-right font-mono text-xs text-slate-200">{fM(p.FY2025A)}M</td>
                  <td className="py-2 px-3 text-right font-mono text-xs">
                    <span className={p.growth > 0 ? "text-emerald-400" : "text-rose-400"}>{p.growth > 0 ? "+" : ""}{p.growth}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
